<!--
This file is part of the project: "Digiziege"
An app for animal-welfare-oriented goat husbandry and contactless weight estimation.
GitLab: https://gitlab.rlp.net/proj-wise2526-digiziege/digiziege

Copyright (c) 2026 Digiziege Team TH-Bingen

Description:
Companion app for welfare-oriented goat husbandry featuring contactless weight estimation via camera,
master data management and health tracking.
Built as a PWA with a local-first approach to ensure data privacy.

LICENSE INFORMATION:
This program is free software: you can redistribute it and/or modify it under the terms of the
GNU Affero General Public License as published by the Free Software Foundation,
either version 3 of the License, or (at your option) any later version.

This program is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
See the GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License along with this program.
If not, see <https://www.gnu.org/licenses/>.
-->

<template>
  <div
    class="min-h-screen bg-[var(--color-bg)] text-[var(--color-text)] flex flex-col pb-20">
    <!-- LOADING -->
    <Loading v-if="loading" :text="$t('views.health.loading')" />

    <!-- ERROR -->
    <div v-else-if="error" class="text-red-600 text-center">
      {{ error }}
    </div>

    <!-- CONTENT -->
    <div v-else class="flex-1 px-4">
      <!-- Add Button -->
      <button
        @click="openAddForm"
        class="w-full mb-4 bg-[var(--color-primary)] text-green-600 rounded-lg p-4 flex items-center justify-center gap-2 hover:opacity-90 transition-opacity">
        <component :is="PlusCircle" class="w-5 h-5" />
        {{ $t('views.health.treatmentsDetail.addButton') }}
      </button>

      <!-- Records List -->
      <div v-if="treatments.length === 0" class="text-center text-gray-500 py-8">
        {{ $t('views.health.treatmentsDetail.noData') }}
      </div>

      <div v-else class="space-y-3">
        <ModuleShell
          v-for="record in treatments"
          :key="record.id"
          :title="record.title"
        >
          <template #action>
            <div class="flex gap-2">
              <button
                @click="openEditForm(record)"
                class="p-2 hover:bg-[var(--color-bg)] rounded-lg transition-colors">
                <component :is="Pencil" class="w-4 h-4" />
              </button>
              <button
                @click="openConfirm(record)"
                class="p-2 hover:bg-red-100 dark:hover:bg-red-900/20 rounded-lg transition-colors text-red-600">
                <component :is="Trash" class="w-4 h-4" />
              </button>
            </div>
          </template>

          <div class="space-y-2">
            <p class="text-sm opacity-80">{{ $t(`views.health.treatmentsDetail.types.${record.type}`) }}</p>
            <p class="text-sm opacity-80">{{ record.description }}</p>
            <p class="text-xs opacity-60">
              {{ formatDate(record.measuredAt) }}
            </p>
          </div>
        </ModuleShell>
      </div>
    </div>
  </div>

  <!-- Add/Edit Dialog -->
  <ValueInput
    :show="showDialog"
    :title="goat?.eartag || ''"
    :subtitle="goat?.name || ''"
    :input-mode="'treatment'"
    :goatId="goatId"
    :show-date-input="true"
    :initial-data="editData"
    @save="handleSave"
    @update:show="closeDialog" />

  <!-- Delete Confirmation Dialog -->
  <ConfirmActionModule
    v-if="showConfirm"
    :title="$t('views.health.treatmentsDetail.deleteConfirm')"
    :confirmMessage="$t('common.buttons.delete')"
    :message="$t('views.health.treatmentsDetail.deleteMessage')"
    variant="danger"
    @confirm="deleteConfirmed"
    @cancel="closeConfirm" />
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { useHealthStore } from '@/stores/health.store.js'
import { useGoatStore } from '@/stores/goat.store.js'
import { storeToRefs } from 'pinia'

import Loading from '@/components/ui/feedback/Loading.vue'
import ModuleShell from '@/components/ui/layout/ModuleShell.vue'
import ConfirmActionModule from '@/components/actions/ConfirmAction.vue'
import ValueInput from '@/components/ui/inputs/ValueInput.vue'
import { Trash, Pencil, PlusCircle } from 'lucide-vue-next'
import { formatDate } from '@/utils/format/dateformatting'

const route = useRoute()
const goatId = Number(route.params.id)

const healthStore = useHealthStore()
const { loading, error } = storeToRefs(healthStore)

const goatStore = useGoatStore()
const goat = ref(null)

const treatments = computed(() => healthStore.treatments(goatId))

const showDialog = ref(false)
const showConfirm = ref(false)
const selectedRecord = ref(null)
const editingRecord = ref(null)
const editData = ref(null)

function openAddForm() {
  editingRecord.value = null
  editData.value = null
  showDialog.value = true
}

function openEditForm(record) {
  editingRecord.value = record
  editData.value = {
    type: record.type,
    title: record.title,
    description: record.description,
    date: record.measuredAt.split('T')[0]
  }
  showDialog.value = true
}

function closeDialog() {
  showDialog.value = false
  editingRecord.value = null
  editData.value = null
}

async function handleSave({ type, title, description, date }) {
  try {
    const data = {
      goatId,
      type,
      title,
      description,
      measuredAt: new Date(date).toISOString()
    }

    if (editingRecord.value) {
      await healthStore.updateHealthRecord(editingRecord.value.id, data)
    } else {
      await healthStore.createHealthRecord(data)
    }

    closeDialog()
  } catch (e) {
    console.error('Error saving health record:', e)
  }
}

function openConfirm(record) {
  selectedRecord.value = record
  showConfirm.value = true
}

function closeConfirm() {
  showConfirm.value = false
}

async function deleteConfirmed() {
  if (!selectedRecord.value) return
  
  try {
    await healthStore.deleteHealthRecord(selectedRecord.value.id)
    closeConfirm()
  } catch (e) {
    console.error('Error deleting health record:', e)
  }
}

onMounted(async () => {
  goat.value = await goatStore.getGoatById(goatId)
  await healthStore.fetchHealthRecordsByGoat(goatId)
})
</script>


